import { lazy, Suspense } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import Home from "../../screens/Home";

const Success = lazy(() => import("../../screens/Success"));
const Cancel = lazy(() => import("../../screens/Cancel"));
const Sign = lazy(() => import("../../screens/Sign"));
const ResetPassword = lazy(() => import("../../screens/ResetPassword"));
const Verification = lazy(() => import("../../screens/Sign/components/Verification"));
const Profile = lazy(() => import("../../screens/Profile"));
const ProfileInformation = lazy(() => import("../../screens/Profile/screens/ProfileInformation"));
const Subscription = lazy(() => import("../../screens/Profile/screens/Subscription"));
const FreeTrial = lazy(() => import("../../screens/Profile/screens/FreeTrial"));
const Password = lazy(() => import("../../screens/Profile/screens/Password"));
const CompanyInfo = lazy(() => import("../../screens/CompanyInfo"));
const Terms = lazy(() => import("../../screens/Terms"));
const PrivacyPolicy = lazy(() => import("../../screens/PrivacyPolicy"));
const RequestDemo = lazy(() => import("../../screens/RequestDemo"));
const Downloads = lazy(() => import("../../screens/Downloads"));

const PageTransition = lazy(() => import("./PageTransition"));

const AnimatedRoutes = () => {
  const location = useLocation();
  const { user, authReady } = useUser();
  const pathname = location.pathname.replace(/\/+$/, "") || "/";
  const needsAuthState = pathname.startsWith("/profile") ||
    ["/sign", "/verification", "/downloads"].includes(pathname);
  if (needsAuthState && !authReady) {
    return <p role="status" style={{ padding: "48px", textAlign: "center" }}>Loading…</p>;
  }
  const redirectState = {
    from: `${location.pathname}${location.search}${location.hash}` || "/",
  };
  
  return (
    <Suspense fallback={<p role="status" style={{ padding: "48px", textAlign: "center" }}>Loading…</p>}>
      <Routes location={location} key={location.pathname}>
        {/* Main Routes */}
        <Route
          path="/"
          element={<Home />}
        />
        <Route
          path="/reset-password"
          element={
            <PageTransition>
              <ResetPassword />
            </PageTransition>
          }
        />
        <Route
          path="/sign"
          element={new URLSearchParams(location.search).get("mode") === "signup"
            ? <Navigate to="/signup/" replace state={location.state} />
            : user ? <Navigate to="/profile" replace /> : <Sign />}
        />
        <Route
          path="/signup"
          element={user ? <Navigate to="/profile" replace /> : <Sign initialMode="signup" />}
        />
        <Route
          path="/verification"
          element={
            !user ? (
              <Navigate to="/sign/" replace state={redirectState} />
            ) : user.emailVerified ? (
              <Navigate to="/profile" replace />
            ) : (
              <PageTransition>
                <Verification />
              </PageTransition>
            )
          }
        />
        <Route
          path="/profile"
          element={
            user ? (
              user.emailVerified ? (
                <Profile />
              ) : (
                <Navigate to="/verification" replace state={{ email: user.email }} />
              )
            ) : (
              <Navigate to="/sign/" replace state={redirectState} />
            )
          }
          >
          <Route index element={<ProfileInformation />} />
          <Route path="subscription" element={<Subscription />} />
          <Route path="free-trial" element={<FreeTrial />} />
          <Route path="password" element={<Password />} />
        </Route>
        <Route
          path="/company-info"
          element={
            <PageTransition>
              <CompanyInfo />
            </PageTransition>
          }
        />
        {/* Other Routes */}
        <Route
          path="/pricing"
          element={<Navigate to="/#pricing" replace />}
        />
        <Route
          path="/downloads"
          element={
            user ? (
              <PageTransition>
                <Downloads />
              </PageTransition>
            ) : (
              <Navigate to="/sign/" replace state={redirectState} />
            )
          }
        />
        <Route
          path="/features"
          element={<Navigate to="/#features" replace />}
        />
        <Route
          path="/success"
          element={
            <PageTransition>
              <Success />
            </PageTransition>
          }
        />
        <Route
          path="/cancel"
          element={
            <PageTransition>
              <Cancel />
            </PageTransition>
          }
        />
        <Route
          path="/terms"
          element={
            <PageTransition>
              <Terms />
            </PageTransition>
          }
        />
        <Route
          path="/privacy-policy"
          element={
            <PageTransition>
              <PrivacyPolicy />
            </PageTransition>
          }
        />
        <Route
          path="/request-demo"
          element={
            <PageTransition>
              <RequestDemo />
            </PageTransition>
          }
        />
      </Routes>
    </Suspense>
  );
};

export default AnimatedRoutes;
